import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import KanbanContext from '../../KanbanContext'
import { StatusKanbanSearchCondition } from '../../hooks/useSearchCondition'
import { DateTermVO } from '../../../../../domain/value-object/DateTermVO'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import { FontSize } from '../../../../../styles/commonStyles'
import SearchConditionSelect from './SearchConditionSelect'
import SearchConditionAutocomplete from './SearchConditionAutocomplete'
import { DateRangeItem } from '../Card/DateRangeItem'
import useAnchorButton from '../Filter/useAnchorButton'

type Props = {
  searchCondition: StatusKanbanSearchCondition
  onTeamChange: (uuid?: string) => void
  onSprintChange: (uuid?: string) => void
  onScheduledEndDateChange: (v?: DateTermVO) => void
}

const SearchConditionPopper = ({
  searchCondition,
  onTeamChange,
  onSprintChange,
  onScheduledEndDateChange,
}: Props) => {
  const context = useContext(KanbanContext)
  const { isOpen, anchorEl, handleClose, toggleOpen } = useAnchorButton()
  const paperRef = useRef<HTMLDivElement>(null)
  const [isChildEditing, setIsChildEditing] = useState(false)

  const handleChildEditStart = useCallback(() => setIsChildEditing(true), [])
  const handleChildEditEnd = useCallback(() => setIsChildEditing(false), [])

  const filteredSprints = useMemo(() => {
    if (!searchCondition.teamUuid) {
      return context.sprints
    }
    return context.sprints.filter(
      sprint => sprint.teamUuid === searchCondition.teamUuid
    )
  }, [context.sprints, searchCondition.teamUuid])

  return (
    <ClickAwayListener
      onClickAway={() => {
        isOpen && !isChildEditing && handleClose()
      }}
    >
      <Box>
        <Tooltip title={intl.formatMessage({ id: 'kanban.searchCondition' })}>
          <StyledIconButton onClick={toggleOpen}>
            <TuneIcon />
          </StyledIconButton>
        </Tooltip>
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          disablePortal={true}
        >
          <StyledPaper ref={paperRef}>
            <SearchConditionItem>
              <SearchConditionLabel>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.team' })}
              </SearchConditionLabel>
              <SearchConditionSelect
                value={searchCondition.teamUuid}
                options={context.teams}
                onChange={onTeamChange}
                onOpen={handleChildEditStart}
                onClose={handleChildEditEnd}
              />
            </SearchConditionItem>
            <SearchConditionItem>
              <SearchConditionLabel>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.sprint' })}
              </SearchConditionLabel>
              <SearchConditionAutocomplete
                value={searchCondition.sprintUuid}
                options={filteredSprints}
                onChange={onSprintChange}
                onOpen={handleChildEditStart}
                onClose={handleChildEditEnd}
                disabled={!searchCondition.teamUuid}
                placeholder={
                  !searchCondition.teamUuid
                    ? intl.formatMessage({
                        id: 'wbsItemSearchFilterPanel.selectTeam',
                      })
                    : undefined
                }
              />
            </SearchConditionItem>
            <SearchConditionItem>
              <SearchConditionLabel>
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.scheduledEndDate',
                })}
              </SearchConditionLabel>
              <SearchConditionDateRange>
                <DateRangeItem
                  value={searchCondition.scheduledEndDate}
                  onChange={onScheduledEndDateChange}
                  onOpen={handleChildEditStart}
                  onClose={handleChildEditEnd}
                />
              </SearchConditionDateRange>
            </SearchConditionItem>
          </StyledPaper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default SearchConditionPopper

const StyledIconButton = styled(IconButton)({
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  padding: '4px',
  '&:hover': { background: colorPalette.monotone[2] },
})

const StyledPaper = styled(Paper)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
  minWidth: '300px',
})

const SearchConditionItem = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
})

const SearchConditionLabel = styled(Typography)({
  color: colorPalette.monotone[5],
  fontSize: FontSize.MEDIUM,
})

const SearchConditionDateRange = styled('div')({
  padding: '4px 8px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
})
